import { createFileRoute } from "@tanstack/react-router";

import { AdaStatLink } from "@/components/adastat-link";
import { QueryState } from "@/components/query-state";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useGetArbitragesHistory } from "@/generated/api/arbitrages/arbitrages";
import type { ArbitrageHistoryItem } from "@/generated/api/model";

export const Route = createFileRoute("/arbitrages/history")({ component: ArbitragesHistory });

function ArbitragesHistory() {
    const history = useGetArbitragesHistory();

    return (
        <QueryState
            query={history}
            isEmpty={(data) => data.transaction.length === 0}
            emptyMessage="No arbitrage has been executed yet."
        >
            {(data) => (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">Transaction history</h2>
                        <Badge>
                            {data.transaction.length} transaction{data.transaction.length === 1 ? "" : "s"}
                        </Badge>
                    </div>
                    {data.transaction.map((item, index) => (
                        <HistoryCard key={`${item.transaction.id}-${index}`} item={item} />
                    ))}
                </div>
            )}
        </QueryState>
    );
}

// Datums can arrive as a JSON string; show them parsed when they are.
const pretty = (value: unknown): string => {
    if (typeof value === "string") {
        try {
            return JSON.stringify(JSON.parse(value), null, 2);
        } catch {
            return value;
        }
    }
    return JSON.stringify(value ?? null, null, 2);
};

function Section({ title, body }: { title: string; body: string }) {
    return (
        <div className="min-w-0 space-y-1">
            <h3 className="text-xs font-medium uppercase text-muted-foreground">{title}</h3>
            <pre className="max-h-80 overflow-auto rounded-md bg-muted/50 p-2 text-xs break-all whitespace-pre-wrap">
                {body}
            </pre>
        </div>
    );
}

function HistoryCard({ item }: { item: ArbitrageHistoryItem }) {
    return (
        <Card role="article" aria-label="Executed arbitrage">
            <CardHeader className="justify-start text-sm">
                <span className="shrink-0 text-muted-foreground">Transaction</span>
                <AdaStatLink
                    kind="transaction"
                    value={item.transaction.id}
                    label="Transaction id"
                    fit="line"
                    className="min-w-0"
                />
            </CardHeader>
            <CardContent className="grid gap-4 lg:grid-cols-12">
                <div className="lg:col-span-3">
                    <Section title="Arbitrage" body={item.arbitrage_text} />
                </div>
                <div className="lg:col-span-5">
                    <Section title="Trade output" body={pretty(item.transaction.outputs[0])} />
                </div>
                <div className="lg:col-span-4">
                    <Section title="Transaction datums" body={pretty(item.transaction.datums)} />
                </div>
            </CardContent>
        </Card>
    );
}
